export const MIN_SEARCH_LENGTH = 2;
export const MAX_PER_PAGE = 100;
export const MAX_RESTORE_JOBS = 300;

export const LISTING_PER_PAGE = 10;
export const SEARCH_DEBOUNCE_MS = 300;

export const MORE_JOBS_COUNT = 5;

export const CARD_MAX_VISIBLE_TAGS = 4;
export const CARD_VISIBLE_TAGS_MIN = 1;
export const CARD_VISIBLE_TAGS_MAX = 20;

export const DEFAULT_LOGO_HEIGHT_PX = 32;
export const LOGO_HEIGHT_MIN_PX = 16;
export const LOGO_HEIGHT_MAX_PX = 120;

export const THEME_EDITOR_PARAM = 'theme_editor';
export const THEME_EDITOR_PARAM_VALUE = 'open';
export const PREFERS_CONTRAST_QUERY = '(prefers-contrast: more)';
/* lets redirect chains settle so a tracking pixel fires once per landed page */
export const TRACKING_MOUNT_DELAY_MS = 300;

export const EMPTY_SELECTIONS: JobBoardFilterSelections = {
  workModeIds: [],
  employmentTypeIds: [],
  categoryIds: [],
  customFieldIds: {},
  location: '',
  locationType: null,
  radiusKm: null,
};

export const SEARCH_CHIP_ID = 'q';

export const FILTER_CLASSES: {
  optionClass: JobBoardFieldOptionClass;
  key: JobBoardFilterClassKey;
}[] = [
  { optionClass: 'category', key: 'categoryIds' },
  { optionClass: 'work_mode', key: 'workModeIds' },
  {
    optionClass: 'employment_type',
    key: 'employmentTypeIds',
  },
];

export const RADIUS_STEPS_KM: (number | null)[] = [null, 5, 10, 25, 50, 100];

export const SORT_KEYS: JobBoardSortKey[] = ['newest', 'rate_high', 'rate_low'];

export const resolveSortKey = (value?: string | null): JobBoardSortKey =>
  SORT_KEYS.includes(value as JobBoardSortKey)
    ? (value as JobBoardSortKey)
    : 'newest';

export const listingSortParam = (
  sort: JobBoardSortKey
): JobBoardListingSort | undefined => (sort === 'newest' ? undefined : sort);

export const BOARD_ASSET_KEYS: BoardAssetKey[] = [
  'logo',
  'logo_dark',
  'favicon',
  'hero',
  'hero_mobile',
];

/* the banner is shown whole, so the band takes its shape from these numbers and
   the phone variant only narrows it — same height, half the width */
export const HERO_BANNER = { width: 2400, height: 400 };
export const HERO_BANNER_MOBILE = { width: 1200, height: 400 };
export const HERO_BANNER_MAX_WIDTH_PX = 1920;

export const SAVED_JOB_UNDO_MS = 10000;

export const TRACKING_TTL_MS = 48 * 60 * 60 * 1000;

export const ACTION_SELECTOR = 'a, button, [role="button"]';
export const CANDIDATE_PORTAL_ATTRIBUTE = 'data-candidately-portal-link';
export const CANDIDATE_PORTAL_SELECTOR = `[${CANDIDATE_PORTAL_ATTRIBUTE}]`;
export const CANDIDATE_PORTAL_LABEL_ATTRIBUTE = 'data-candidately-portal-label';
export const CANDIDATE_PORTAL_LABEL_SELECTOR = `[${CANDIDATE_PORTAL_LABEL_ATTRIBUTE}]`;
export const CANDIDATE_PORTAL_SLOT_ATTRIBUTE = 'data-candidately-portal-slot';
export const CANDIDATE_PORTAL_SLOT_SELECTOR = `[${CANDIDATE_PORTAL_SLOT_ATTRIBUTE}]`;

export const CUSTOM_CHROME_RESET_CSS = `
  :host { all: initial; display: block; width: 100%; font-family: inherit; }
  html, body { display: block; width: 100%; margin: 0; padding: 0; }
  *, *::before, *::after { box-sizing: border-box; }
  [data-candidately-header-group] { display: contents; }
  [data-candidately-portal-link] { white-space: nowrap; width: auto !important; }
  [data-candidately-mobile-portal][hidden] { display: none !important; }
`;
// Elementor and custom theme toggles open menus via site JS (stripped on import) and carry no aria/Bootstrap hooks.
export const CUSTOM_CHROME_MOBILE_TOGGLE = '#menu-toggle, .loke-menu-icon';
export const CUSTOM_CHROME_MOBILE_HEADER =
  '#mobile-header, [class*="mobile-header"], [id*="mobile-header"]';
export const CUSTOM_CHROME_MOBILE_MENU =
  '#mobile-menu-wrapper, [class*="mobile-menu"], [id*="mobile-menu"]';
export const CUSTOM_CHROME_COLLAPSE_TOGGLE = `.navbar-toggler, .elementor-menu-toggle, .slide-menu__control[data-target], ${CUSTOM_CHROME_MOBILE_TOGGLE}, [data-bs-toggle="collapse"], [data-toggle="collapse"], [aria-controls][aria-expanded]:not([aria-haspopup])`;
export const CUSTOM_CHROME_COLLAPSE_MENU = '.navbar-collapse, .collapse';
export const CUSTOM_CHROME_DROPDOWN_TOGGLE =
  '[data-bs-toggle="dropdown"], .dropdown-toggle';
export const CANDIDATE_PORTAL_STYLE_PROPERTIES = [
  'align-items',
  'appearance',
  'aspect-ratio',
  'background-attachment',
  'background-blend-mode',
  'background-clip',
  'background-color',
  'background-image',
  'background-origin',
  'background-position',
  'background-repeat',
  'background-size',
  'border-bottom',
  'border-left',
  'border-radius',
  'border-right',
  'border-top',
  'box-shadow',
  'box-sizing',
  'clip-path',
  'color',
  'display',
  'filter',
  'flex-basis',
  'flex-direction',
  'flex-grow',
  'flex-shrink',
  'flex-wrap',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'gap',
  'height',
  'justify-content',
  'letter-spacing',
  'line-height',
  'margin',
  'max-width',
  'min-height',
  'min-width',
  'opacity',
  'overflow',
  'padding',
  'position',
  'text-align',
  'text-decoration-color',
  'text-decoration-line',
  'text-decoration-style',
  'text-decoration-thickness',
  'text-shadow',
  'text-transform',
  'transform',
  'transform-origin',
  'vertical-align',
  'white-space',
  'width',
  'word-spacing',
];
export const CANDIDATE_PORTAL_EDITOR_CSS = `
  body * {
    cursor: pointer !important;
  }
  [data-candidately-editor-hover] {
    outline: 0.2rem dashed #2563eb !important;
    outline-offset: 0.2rem !important;
    cursor: pointer !important;
  }
  [data-candidately-editor-selected] {
    outline: 0.3rem solid #2563eb !important;
    outline-offset: 0.3rem !important;
  }
  ${CANDIDATE_PORTAL_SELECTOR} {
    outline-color: #7c3aed !important;
  }
`;
